"use client";

import { useState } from "react";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles,
  Diamond,
  Microscope,
  ArrowUpCircle,
  Sun,
  HeartPulse,
  Droplets,
  ScanFace,
  Moon,
  ShieldPlus,
  Flower2,
  Dumbbell,
  Lightbulb,
  Syringe,
  Zap,
  Waves,
  CircleDot,
  Scissors,
  Focus,
  Hand,
  Footprints,
  Eye,
  SmilePlus,
} from "lucide-react";
import { SectionReveal } from "@/components/common/section-reveal";
import { CARD_VARIANTS, STAGGER_CONTAINER } from "@/lib/animations";
import type { ProcedureTagStat } from "@/types/api";
import { wikiExploreLink } from "@/lib/url";

// 아이콘 매핑 (아이콘만 — 색상은 탭별 통일)
const iconMap: Record<string, React.ElementType> = {
  // Purpose
  주름개선: Sparkles,
  탄력: Diamond,
  모공관리: Microscope,
  리프팅: ArrowUpCircle,
  색소개선: Sun,
  흉터개선: HeartPulse,
  "보습/재생": Droplets,
  안면윤곽: ScanFace,
  미백: Moon,
  여드름치료: ShieldPlus,
  홍조개선: Flower2,
  체형관리: Dumbbell,
  // Method
  보톨리눔톡신: Syringe,
  레이저: Zap,
  고주파: Waves,
  초음파: CircleDot,
  필러: Droplets,
  필링: Sparkles,
  수술: Scissors,
  // Area
  얼굴전체: SmilePlus,
  "눈/눈썹": Eye,
  이마: Focus,
  "턱/턱선": ScanFace,
  종아리: Footprints,
  겨드랑이: Hand,
};

// 탭별 통일 색상 (COLOR_GUIDE.md 참조)
const TAB_COLORS = {
  purpose: {
    from: "from-violet-100",
    to: "to-violet-200",
    text: "text-violet-600",
    bar: "from-violet-500 to-violet-700",
  },
  method: {
    from: "from-teal-100",
    to: "to-teal-200",
    text: "text-teal-700",
    bar: "from-teal-500 to-teal-700",
  },
  area: {
    from: "from-amber-50",
    to: "to-amber-100",
    text: "text-amber-700",
    bar: "from-amber-500 to-amber-700",
  },
} as const;

const TABS = [
  { key: "purpose" as const, label: "목적" },
  { key: "method" as const, label: "방법" },
  { key: "area" as const, label: "부위" },
];

type TabKey = keyof typeof TAB_COLORS;

interface CategoryExplorerProps {
  tagStats: Record<"purpose" | "method" | "area", ProcedureTagStat[]>;
}

export function CategoryExplorer({ tagStats }: CategoryExplorerProps) {
  const [activeTab, setActiveTab] = useState<TabKey>("purpose");
  if (!tagStats) return null;
  const tags = tagStats[activeTab] || [];
  const top6 = tags.slice(0, 6);
  const rest = tags.slice(6);
  const colors = TAB_COLORS[activeTab];

  return (
    <SectionReveal>
      <section className="px-5 py-8">
        <h2 className="mb-5 flex items-center gap-2.5 text-xl font-bold tracking-tight">
          <span className={`h-5 w-1 rounded-sm bg-gradient-to-b ${colors.bar}`} />
          어떤 고민이 있으신가요?
        </h2>

        {/* 탭 바 */}
        <div className="relative mb-5 flex gap-1 rounded-xl bg-gray-100 p-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative flex-1 rounded-lg py-2.5 text-center text-sm font-semibold transition-colors ${
                activeTab === tab.key ? "text-gray-900" : "text-gray-500"
              }`}
            >
              {activeTab === tab.key && (
                <motion.div
                  layoutId="tab-indicator"
                  className="absolute inset-0 rounded-lg bg-white shadow-sm"
                  transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
                />
              )}
              <span className="relative z-10">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* 카테고리 카드 그리드 */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={STAGGER_CONTAINER}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
          >
            <div className="grid grid-cols-3 gap-2.5">
              {top6.map((tag) => {
                const Icon = iconMap[tag.name] || Lightbulb;
                return (
                  <motion.div key={tag.name} variants={CARD_VARIANTS}>
                    <Link
                      href={wikiExploreLink(activeTab, tag.name)}
                      className="flex flex-col items-center gap-2 rounded-2xl border border-gray-100 bg-white p-4 transition-all hover:border-violet-200 hover:shadow-md active:scale-[0.97]"
                    >
                      <div
                        className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${colors.from} ${colors.to}`}
                      >
                        <Icon className={`h-5 w-5 ${colors.text}`} strokeWidth={1.8} />
                      </div>
                      <span className="text-center text-sm leading-tight font-semibold tracking-tight text-gray-900">
                        {tag.name}
                      </span>
                      <span className="text-muted-foreground text-xs font-medium">
                        {tag.count}개
                      </span>
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            {rest.length > 0 && (
              <div className="scrollbar-none -mx-5 mt-4 flex gap-2 overflow-x-auto px-5 pb-1">
                {rest.map((tag) => (
                  <Link
                    key={tag.name}
                    href={wikiExploreLink(activeTab, tag.name)}
                    className="shrink-0 rounded-full border border-gray-200 bg-white px-3.5 py-2 text-xs font-medium text-gray-600 transition-colors hover:border-violet-300 hover:bg-violet-50 hover:text-violet-600"
                  >
                    {tag.name} <span className="text-gray-400">{tag.count}</span>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </section>
    </SectionReveal>
  );
}
